import { Check, ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "#/components/ui/tooltip.tsx";
import { cn } from "#/lib/utils";

export type StepStatus = "active" | "completed" | "upcoming";
export type FlowStatus = "origin" | "active" | "completed" | "upcoming";

export type FlowStep = {
	id: string;
	label: string;
	description: string;
	status: FlowStatus;
};

export function FlowIndicator({ steps }: { steps: Array<FlowStep> }) {
	return (
		<div className="pb-1">
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-label2">
				{steps.map((step, index) => (
					<div key={step.id} className="contents">
						<FlowStepMarker step={step} index={index + 1} />
						{index < steps.length - 1 ? (
							<span aria-hidden className="text-trade-text-faint/70">
								&gt;
							</span>
						) : null}
					</div>
				))}
			</div>
		</div>
	);
}

export function FlowStepPanel({
	step,
	title,
	status,
	expanded,
	onToggle,
	preview,
	children,
}: {
	step: number;
	title: string;
	status: StepStatus;
	expanded: boolean;
	onToggle: () => void;
	preview?: ReactNode;
	children: ReactNode;
}) {
	return (
		<section
			className={cn(
				"overflow-hidden rounded-[18px] border transition-[border-color,background-color,box-shadow]",
				status === "active"
					? "border-primary/25 bg-trade-surface shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--color-theme-primary-normal)_6%,transparent)]"
					: "border-trade-border-subtle bg-trade-surface/80",
			)}
		>
			<button
				type="button"
				onClick={onToggle}
				aria-expanded={expanded}
				className="flex w-full items-start gap-3 px-4 py-4 text-left cursor-pointer"
			>
				<StepBullet step={step} status={status} />

				<div className="min-w-0 flex-1">
					<div className="flex items-center justify-between gap-3">
						<span className="truncate text-body1-bold text-trade-text-strong">
							{title}
						</span>
						<div className="flex shrink-0 items-center">
							<ChevronDown
								className={cn(
									"h-4 w-4 text-trade-text-secondary transition-transform",
									expanded && "rotate-180",
								)}
							/>
						</div>
					</div>

					{preview && (
						<div
							className={cn(
								"mt-2 text-caption1 text-trade-text-secondary transition-opacity",
								expanded && "opacity-70",
							)}
						>
							{preview}
						</div>
					)}
				</div>
			</button>

			<div
				className={cn(
					"grid transition-[grid-template-rows,opacity]",
					expanded
						? "grid-rows-[1fr] opacity-100"
						: "grid-rows-[0fr] opacity-0",
				)}
			>
				<div className="overflow-hidden">
					<div className="border-t border-trade-border-subtle px-4 py-4">
						{children}
					</div>
				</div>
			</div>
		</section>
	);
}

function FlowStepMarker({ step, index }: { step: FlowStep; index: number }) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span
					className={cn(
						"inline-flex max-w-full cursor-pointer items-center rounded-sm bg-transparent p-0 text-left transition-colors outline-none",
						step.status === "origin" && "text-trade-text-secondary",
						step.status === "active" &&
							"text-label2-bold text-primary underline underline-offset-4",
						step.status === "completed" && "text-trade-text-strong",
						step.status === "upcoming" && "text-trade-text-faint",
					)}
					aria-current={step.status === "active" ? "step" : undefined}
				>
					{index > 1 && step.status === "completed" ? (
						<Check className="mr-1 h-3.5 w-3.5 text-primary" />
					) : null}
					<span className="break-words whitespace-normal">{step.label}</span>
				</span>
			</TooltipTrigger>
			<TooltipContent
				side="bottom"
				align="center"
				showArrow={false}
				className="max-w-[min(280px,calc(100vw-2rem))] whitespace-normal break-words text-center"
			>
				{step.description}
			</TooltipContent>
		</Tooltip>
	);
}

function StepBullet({ step, status }: { step: number; status: StepStatus }) {
	return (
		<div
			className={cn(
				"mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-label2-bold",
				status === "active" &&
					"border-primary/40 bg-primary text-primary-foreground",
				status === "completed" &&
					"border-primary/20 bg-primary/10 text-primary",
				status === "upcoming" &&
					"border-trade-border-subtle bg-trade-surface-inset text-trade-text-faint",
			)}
		>
			{step}
		</div>
	);
}
